// ─────────────────────────────────────────────────────────────────────────
// src/lib/theme.ts
//
// Hell/Dunkel-Darstellung — eine Quelle für alle Stellen (10.10.2026).
//
// Drei Stufen, die der Knopf reihum schaltet: Auto → Hell → Dunkel.
// „Auto" folgt der Einstellung des Geräts (`prefers-color-scheme`) und
// wechselt auch bei offener App sofort mit.
//
// Vorher: Die Uhrzeit (20–7 Uhr dunkel) entschied nur, solange nie jemand
// auf Sonne/Mond geklickt hatte. Der erste Klick schrieb `drpx_dark` fest
// in den Browser — danach war die Automatik für immer aus, ohne Weg zurück.
// Der alte Schlüssel wird beim Start einmalig entfernt; alle starten
// dadurch wieder auf „Auto".
// ─────────────────────────────────────────────────────────────────────────

import { safeGetLS, safeSetLS, safeRemoveLS } from './storage.js';
import type { TranslateFn } from '../types';

/** Darstellungs-Stufe, die der Nutzer gewählt hat. */
export type ThemeMode = 'auto' | 'light' | 'dark';

/** localStorage-Schlüssel der gewählten Stufe. */
const THEME_KEY = 'drpx_theme';

/** Alter Schlüssel (bis 10.10.2026): '1' = dunkel, '0' = hell, fehlt = Uhrzeit. */
const LEGACY_DARK_KEY = 'drpx_dark';

const DARK_QUERY = '(prefers-color-scheme: dark)';

const MODES: readonly ThemeMode[] = ['auto', 'light', 'dark'];

/**
 * Liest die gespeicherte Stufe. Fehlt sie oder ist sie unbekannt: „Auto".
 *
 * @returns gespeicherte Stufe oder `'auto'`
 */
export function readThemeMode(): ThemeMode {
  const s = safeGetLS(THEME_KEY);
  return MODES.includes(s as ThemeMode) ? (s as ThemeMode) : 'auto';
}

/**
 * Speichert die gewählte Stufe im Browser.
 *
 * @param mode neue Stufe
 */
export function saveThemeMode(mode: ThemeMode): void {
  safeSetLS(THEME_KEY, mode);
}

/** Entfernt den alten Schalter `drpx_dark` (siehe Kopfkommentar). */
export function removeLegacyThemeKey(): void {
  safeRemoveLS(LEGACY_DARK_KEY);
}

/**
 * Nächste Stufe beim Tippen auf den Knopf: Auto → Hell → Dunkel → Auto.
 *
 * @param mode aktuelle Stufe
 * @returns folgende Stufe
 */
export function nextThemeMode(mode: ThemeMode): ThemeMode {
  if (mode === 'auto') return 'light';
  if (mode === 'light') return 'dark';
  return 'auto';
}

/**
 * Ist das Gerät auf dunkel gestellt? Ohne `matchMedia` (sehr alte Browser,
 * Testumgebung): dunkel — das war schon vorher der Rückfallwert.
 *
 * @returns `true` bei dunkler Geräte-Einstellung
 */
export function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return window.matchMedia(DARK_QUERY).matches;
}

/**
 * Meldet jeden Wechsel der Geräte-Einstellung.
 *
 * @param onChange erhält `true`, wenn das Gerät auf dunkel wechselt
 * @returns Funktion zum Abmelden (für das Aufräumen in `useEffect`)
 */
export function watchSystemDark(onChange: (dark: boolean) => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return function () { /* nichts zu beobachten */ };
  }
  const mql = window.matchMedia(DARK_QUERY);
  const handler = function (e: MediaQueryListEvent): void { onChange(e.matches); };
  // Safari vor Version 14 kennt nur addListener/removeListener.
  if (typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', handler);
    return function () { mql.removeEventListener('change', handler); };
  }
  mql.addListener(handler);
  return function () { mql.removeListener(handler); };
}

/**
 * Ergibt sich aus Stufe und Geräte-Einstellung eine dunkle Darstellung?
 *
 * @param mode       gewählte Stufe
 * @param systemDark Geräte-Einstellung (nur bei `'auto'` maßgeblich)
 * @returns `true` = dunkel
 */
export function resolveDark(mode: ThemeMode, systemDark: boolean): boolean {
  if (mode === 'auto') return systemDark;
  return mode === 'dark';
}

/**
 * Symbol der aktuellen Stufe (der Knopf zeigt, was GERADE gilt).
 *
 * @param mode aktuelle Stufe
 * @returns Name für `<Icon name>`
 */
export function themeModeIcon(mode: ThemeMode): string {
  if (mode === 'light') return 'sun';
  if (mode === 'dark') return 'moon';
  return 'theme-auto';
}

/**
 * Übersetzter Name der Stufe („Auto", „Hell", „Dunkel").
 * Bewusst direkte `t()`-Aufrufe, damit `check-i18n.mjs` sie findet.
 *
 * @param mode aktuelle Stufe
 * @param t    Übersetzungsfunktion
 * @returns Name in der Sprache von `t`
 */
export function themeModeLabel(mode: ThemeMode, t: TranslateFn): string {
  if (mode === 'light') return t('themeHell');
  if (mode === 'dark') return t('themeDunkel');
  return t('themeAuto');
}

/**
 * Hinweistext für den Knopf (Tooltip und Screenreader), z. B.
 * „Darstellung: Auto".
 *
 * @param mode aktuelle Stufe
 * @param t    Übersetzungsfunktion
 * @returns vollständiger Hinweistext
 */
export function themeButtonTitle(mode: ThemeMode, t: TranslateFn): string {
  return t('themeTitel') + ': ' + themeModeLabel(mode, t);
}
